import Table from "@mui/material/Table";
import React from "react";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { TableHeadComponent } from "../../common/TableHead";
import { useAppStore } from "../../../appStore";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { OrangeTextButton } from "../../common/OrangeTextButton";

export const SlicingTabEditMode = () => {
  const {
    cameraStreams,
    cameraStreamsObject,
    slicingChanges,
    showSlicingWarningMessage,
    setSlicingChangesMade,
    setSlicingChanges,
  } = useEditCameraStore();

  const { userPermissions } = useAppStore();

  const [showMaxSlices, setShowMaxSlices] = React.useState<
    Record<string | number, boolean>
  >({});

  const [maxSlicesValues, setMaxSlicesValues] = React.useState<
    Record<string | number, string>
  >({});

  const [sliceValues, setSliceValues] = React.useState<
    Record<string | number, boolean>
  >({});

  const [slicingWarningDialogOpen, setSlicingWarningDialogOpen] =
    React.useState(false);
  const [pendingSliceChange, setPendingSliceChange] = React.useState<{
    streamId: number;
    streamIndex: number;
    checked: boolean;
  } | null>(null);

  const applySliceChange = (
    streamId: number,
    streamIndex: number,
    checked: boolean
  ) => {
    setSlicingChangesMade(true);
    const existingIndex = slicingChanges.findIndex(
      (change) => change.streamId === streamId
    );
    if (existingIndex !== -1) {
      const updated = [...slicingChanges];
      updated[existingIndex] = {
        streamId: streamId,
        slice: checked,
        streamIndex: streamIndex,
      };
      setSlicingChanges(updated);
    } else {
      setSlicingChanges([
        ...slicingChanges,
        {
          streamId: streamId,
          slice: checked,
          streamIndex: streamIndex,
        },
      ]);
    }
    setShowMaxSlices((prev) => ({
      ...prev,
      [streamId]: checked,
    }));
    setSliceValues((prev) => ({
      ...prev,
      [streamId]: checked,
    }));
  };

  const handleSlicingWarningConfirm = () => {
    if (pendingSliceChange) {
      applySliceChange(
        pendingSliceChange.streamId,
        pendingSliceChange.streamIndex,
        pendingSliceChange.checked
      );
    }
    setSlicingWarningDialogOpen(false);
    setPendingSliceChange(null);
  };

  const handleSlicingWarningCancel = () => {
    setSlicingWarningDialogOpen(false);
    setPendingSliceChange(null);
  };

  React.useEffect(() => {
    cameraStreams?.forEach((stream: any, index: number) => {
      setShowMaxSlices((prev) => ({
        ...prev,
        [stream.id]: cameraStreamsObject[index]?.slice ?? false,
      }));
      setMaxSlicesValues((prev) => ({
        ...prev,
        [stream.id]: cameraStreamsObject[index]?.max_slices?.toString() ?? "",
      }));
      setSliceValues((prev) => ({
        ...prev,
        [stream.id]: cameraStreamsObject[index]?.slice ?? false,
      }));
    });
  }, [cameraStreams, cameraStreamsObject]);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHeadComponent
          titlesArray={["Stream", "Stream Products", "Slicing", "Max Slices"]}
        />
        <TableBody>
          {cameraStreams?.map((stream: any, index: number) => (
            <TableRow key={stream.id}>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: "25%" }}
              >
                {stream.name}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: "25%" }}
              >
                {stream.product}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: "25%" }}
              >
                <Switch
                  checked={
                    sliceValues[stream.id] ??
                    cameraStreamsObject[index]?.slice ??
                    false
                  }
                  color="warning"
                  disabled={false}
                  onChange={(event: any) => {
                    const isChecked = event.target.checked;

                    // Show warning dialog for non-super users enabling slicing on non-gun products
                    if (
                      !userPermissions.super_user &&
                      stream.name !== "production_gun" &&
                      isChecked
                    ) {
                      setPendingSliceChange({
                        streamId: stream.id,
                        streamIndex: index,
                        checked: isChecked,
                      });
                      setSlicingWarningDialogOpen(true);
                      return;
                    }

                    applySliceChange(stream.id, index, isChecked);
                  }}
                />
              </TableCell>
              {!userPermissions.super_user ? (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: "25%" }}
                >
                  {cameraStreamsObject[index]?.slice
                    ? cameraStreamsObject[index]?.max_slices
                    : ""}
                  {cameraStreamsObject[index]?.slice &&
                    cameraStreamsObject[index]?.max_slices &&
                    (cameraStreamsObject[index]?.max_slices ?? 0) <= 4 &&
                    showSlicingWarningMessage && (
                      <div>
                        <div className="text-red-500 text-[12px]">
                          Your site/camera may require a higher number
                        </div>
                        <div className="text-red-500 text-[12px]">
                          of slices. Please contact Support to inquire.
                        </div>
                      </div>
                    )}
                </TableCell>
              ) : (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: "25%" }}
                >
                  {/* {cameraStreamsObject[index]?.max_slices} */}
                  <TextField
                    value={maxSlicesValues[stream.id] ?? ""}
                    variant="standard"
                    sx={{ width: 200 }}
                    type="number"
                    inputProps={{ min: 2 }}
                    error={
                      maxSlicesValues[stream.id] !== "" &&
                      !isNaN(parseInt(maxSlicesValues[stream.id], 10)) &&
                      parseInt(maxSlicesValues[stream.id], 10) < 2
                    }
                    helperText={
                      maxSlicesValues[stream.id] !== "" &&
                      !isNaN(parseInt(maxSlicesValues[stream.id], 10)) &&
                      parseInt(maxSlicesValues[stream.id], 10) < 2
                        ? "Value must be 2 or greater"
                        : "Minimum value is 2"
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = event.target.value;
                      const newMaxSlices = parseInt(inputValue, 10);

                      // Update local state for controlled input
                      setMaxSlicesValues((prev) => ({
                        ...prev,
                        [stream.id]: inputValue,
                      }));

                      // Only update store if value is valid (>= 2)
                      if (
                        inputValue === "" ||
                        isNaN(newMaxSlices) ||
                        newMaxSlices < 2
                      ) {
                        return;
                      }

                      setSlicingChangesMade(true);
                      const existingIndex = slicingChanges.findIndex(
                        (change) => change.streamId === stream.id
                      );
                      if (existingIndex !== -1) {
                        const updated = [...slicingChanges];
                        updated[existingIndex] = {
                          ...updated[existingIndex],
                          max_slices: newMaxSlices,
                        };
                        setSlicingChanges(updated);
                      } else {
                        setSlicingChanges([
                          ...slicingChanges,
                          {
                            streamId: stream.id,
                            slice: cameraStreamsObject[index]?.slice ?? false,
                            streamIndex: index,
                            max_slices: newMaxSlices,
                          },
                        ]);
                      }
                    }}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={slicingWarningDialogOpen}
        onClose={handleSlicingWarningCancel}
      >
        <DialogTitle>Slicing Cost Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please note that slicing for non-gun products incur an additional
            cost. Please contact your Account Manager if you have any additional
            questions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <OrangeTextButton
            onClick={handleSlicingWarningCancel}
            text="Cancel"
            disabled={false}
          />
          <OrangeTextButton
            onClick={handleSlicingWarningConfirm}
            text="Continue"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};
